// WatchDemoModal.jsx
//
// Full-screen modal that wraps MarketingHeroDemo for the "Watch demo"
// CTA. Mounted once at the marketing-surface root (in AppWrapper's
// MarketingRouter); any button anywhere on the site can open it by
// dispatching a window event:
//
//   window.dispatchEvent(new CustomEvent('docketflow:openDemo'))
//
// The reusable <WatchDemoButton /> component below already does that,
// so callers just render <WatchDemoButton /> wherever they want a
// "Watch demo" button.
//
// Why a window event instead of React Context:
//   * No prop drilling — works from deeply nested components
//   * No need to re-architect the whole marketing surface around a
//     provider that wraps everything
//   * Trivial to add additional triggers later (e.g. a "Watch demo"
//     button in an in-app help center) without touching the modal
//
// Closes via:
//   * ESC key
//   * Click outside the demo card
//   * × button in the top-right
//
// Once open, the body's `overflow` is locked so the page underneath
// doesn't scroll. Restored on close.

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { X, Play } from 'lucide-react';
import { BRAND } from './marketingTokens';
import MarketingHeroDemo from './MarketingHeroDemo';
import { trackDemoEvent } from '../../lib/db';

// Tiny UUID-v4 generator. We don't want to add a `uuid` dependency for
// a single use site; crypto.randomUUID() ships in every modern browser
// since 2022 (Chrome 92, Safari 15.4, Firefox 95). Fall back to a
// timestamp-based pseudo-ID for the rare visitor on an ancient browser.
function newSessionId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    try { return crypto.randomUUID(); } catch { /* fallthrough */ }
  }
  // Fallback — not a real UUID but unique enough for analytics on a
  // browser that doesn't support crypto.randomUUID. Postgres will
  // accept it because the column is UUID-typed and we shape it like
  // a UUID; but if it's malformed the INSERT will silently fail and
  // the visitor's session won't be tracked. Acceptable.
  const hex = (n) => Math.floor(Math.random() * 16).toString(16).repeat(n);
  return `${hex(8)}-${hex(4)}-4${hex(3)}-${hex(4)}-${hex(12)}`;
}

export default function WatchDemoModal() {
  const [open, setOpen] = useState(false);

  // Analytics state. sessionId is generated fresh each time the modal
  // opens — closing and re-opening counts as a new session. openedAt is
  // captured so we can compute dwell on close. scenesSeenRef tracks
  // which scene indices have already fired a 'scene_viewed' event so
  // we don't double-count when the visitor manually jumps back via
  // the dots/prev button.
  const sessionIdRef   = useRef(null);
  const openedAtRef    = useRef(0);
  const scenesSeenRef  = useRef(new Set());

  // Listen for the open event from any button anywhere on the site.
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener('docketflow:openDemo', onOpen);
    return () => window.removeEventListener('docketflow:openDemo', onOpen);
  }, []);

  // Initialize / reset analytics state when the modal opens. Fire the
  // 'opened' event so we have one row per modal session even if the
  // visitor closes before any scene renders (rare but possible).
  useEffect(() => {
    if (!open) return undefined;
    sessionIdRef.current  = newSessionId();
    openedAtRef.current   = Date.now();
    scenesSeenRef.current = new Set();
    trackDemoEvent(sessionIdRef.current, 'opened');
    return undefined;
  }, [open]);

  // Close handler — fires a 'closed' event with the dwell time, then
  // resets the React state. Wrapped in useCallback so the multiple
  // close paths (ESC, backdrop click, × button, footer "Keep exploring",
  // footer "I'm ready" navigation) all use the same code path.
  const closeWithTracking = useCallback(() => {
    if (sessionIdRef.current) {
      const dwellMs = Math.max(0, Date.now() - openedAtRef.current);
      trackDemoEvent(sessionIdRef.current, 'closed', { dwellMs });
    }
    setOpen(false);
  }, []);

  // Scene-change callback — called by MarketingHeroDemo whenever the
  // active scene index changes. Deduped via scenesSeenRef so manually
  // jumping back to an earlier scene doesn't double-count.
  const onSceneChange = useCallback((sceneIndex) => {
    if (!sessionIdRef.current) return;
    if (scenesSeenRef.current.has(sceneIndex)) return;
    scenesSeenRef.current.add(sceneIndex);
    trackDemoEvent(sessionIdRef.current, 'scene_viewed', { sceneIndex });
  }, []);

  // ESC key to close. Routes through closeWithTracking so the 'closed'
  // event still fires.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') closeWithTracking(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, closeWithTracking]);

  // Lock body scroll while modal is open so the page underneath
  // doesn't drift around when the user uses the trackpad.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="DocketFlow product demo"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8"
      style={{
        background: 'rgba(15,23,42,0.78)',
        backdropFilter: 'saturate(140%) blur(8px)',
        WebkitBackdropFilter: 'saturate(140%) blur(8px)',
        animation: 'wdm-backdrop-fade 0.25s ease both',
      }}
      onClick={closeWithTracking}
    >
      <style>{`
        @keyframes wdm-backdrop-fade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes wdm-content-pop {
          from { opacity: 0; transform: scale(0.96) translateY(8px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>

      {/* Close button — fixed in the top-right of the viewport so it's
          always accessible regardless of the modal's content height. */}
      <button
        onClick={(e) => { e.stopPropagation(); closeWithTracking(); }}
        aria-label="Close demo"
        className="fixed top-4 right-4 sm:top-6 sm:right-6 w-10 h-10 rounded-full flex items-center justify-center transition-all"
        style={{
          background: 'rgba(255,255,255,0.10)',
          color: '#fff',
          border: '1px solid rgba(255,255,255,0.20)',
          backdropFilter: 'blur(8px)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(255,255,255,0.20)';
          e.currentTarget.style.transform = 'scale(1.05)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(255,255,255,0.10)';
          e.currentTarget.style.transform = 'scale(1)';
        }}
      >
        <X size={18} />
      </button>

      {/* Content. stopPropagation on click so users can interact with
          the demo without the backdrop's onClick closing it. */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-5xl"
        style={{ animation: 'wdm-content-pop 0.32s cubic-bezier(.22,1,.36,1) both' }}
      >
        {/* Title strip above the demo — helps the modal feel like a
            dedicated "watching a demo" experience, not just a popup. */}
        <div className="mb-4 text-center">
          <p
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold mb-3"
            style={{ background: 'rgba(249,115,22,0.20)', color: '#FED7AA' }}
          >
            <Play size={11} fill="currentColor" />
            Watch the 30-second demo
          </p>
          <h2
            className="text-2xl sm:text-3xl font-bold text-white"
            style={{ textShadow: '0 2px 12px rgba(0,0,0,0.30)' }}
          >
            From client intake to paid invoice — in cedis, in one place.
          </h2>
        </div>

        <MarketingHeroDemo onSceneChange={onSceneChange} />

        {/* Footer affordances — secondary CTA inside the modal so a
            visitor who's just finished the demo has a clear next step. */}
        <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            onClick={() => {
              closeWithTracking();
              window.location.hash = '#/book-demo';
              window.scrollTo(0, 0);
            }}
            className="inline-flex items-center justify-center gap-2 px-6 py-2.5 text-sm font-semibold rounded-xl transition-all"
            style={{
              background: `linear-gradient(135deg, ${BRAND.orange} 0%, ${BRAND.orangeDark} 100%)`,
              color: '#fff',
              boxShadow: '0 6px 20px rgba(249,115,22,0.35)',
            }}
          >
            I'm ready — book a real demo
          </button>
          <button
            onClick={closeWithTracking}
            className="inline-flex items-center justify-center gap-2 px-6 py-2.5 text-sm font-semibold rounded-xl transition-all"
            style={{
              background: 'transparent',
              color: 'rgba(255,255,255,0.85)',
              border: '1.5px solid rgba(255,255,255,0.25)',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            Keep exploring
          </button>
        </div>

        {/* Helper hint — accessibility + keyboard discoverability. */}
        <p
          className="mt-4 text-center text-[11px]"
          style={{ color: 'rgba(255,255,255,0.55)' }}
        >
          Press <kbd className="px-1.5 py-0.5 rounded text-[10px]" style={{ background: 'rgba(255,255,255,0.10)', color: '#fff' }}>Esc</kbd> or click outside to close
        </p>
      </div>
    </div>
  );
}

// ─── Reusable trigger button ────────────────────────────────────────
// Drop <WatchDemoButton /> anywhere to add a "Watch demo" CTA. Accepts
// an optional `variant` prop ('orange' | 'outline' | 'ghost') to fit
// whatever context it's in.

export function WatchDemoButton({ variant = 'outline', size = 'md', label = 'Watch demo (30s)' }) {
  const open = () => {
    window.dispatchEvent(new CustomEvent('docketflow:openDemo'));
  };

  const sizing = {
    sm: { px: 'px-3 py-1.5', text: 'text-xs',  icon: 11 },
    md: { px: 'px-5 py-2.5', text: 'text-sm',  icon: 13 },
    lg: { px: 'px-7 py-3.5', text: 'text-sm',  icon: 15 },
  }[size] || { px: 'px-5 py-2.5', text: 'text-sm', icon: 13 };

  let style;
  if (variant === 'orange') {
    style = {
      background: `linear-gradient(135deg, ${BRAND.orange} 0%, ${BRAND.orangeDark} 100%)`,
      color: '#fff',
      boxShadow: '0 6px 20px rgba(249,115,22,0.35)',
    };
  } else if (variant === 'ghost') {
    style = {
      background: 'transparent',
      color: '#fff',
      border: '1.5px solid rgba(255,255,255,0.25)',
    };
  } else {
    // outline (default) — pairs with the orange "Book a demo" CTA in
    // the hero, so this is the secondary action.
    style = {
      background: BRAND.bgWhite,
      color: BRAND.blueDeep,
      border: `1.5px solid ${BRAND.border}`,
    };
  }

  return (
    <button
      type="button"
      onClick={open}
      className={`inline-flex items-center justify-center gap-2 ${sizing.px} ${sizing.text} font-semibold rounded-xl transition-all w-full sm:w-auto`}
      style={style}
      onMouseEnter={(e) => {
        if (variant === 'outline') {
          e.currentTarget.style.borderColor = BRAND.blueUi;
          e.currentTarget.style.background = BRAND.blueGhost;
        } else if (variant === 'ghost') {
          e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
        } else {
          e.currentTarget.style.transform = 'translateY(-1px)';
          e.currentTarget.style.boxShadow = '0 10px 28px rgba(249,115,22,0.45)';
        }
      }}
      onMouseLeave={(e) => {
        if (variant === 'outline') {
          e.currentTarget.style.borderColor = BRAND.border;
          e.currentTarget.style.background = BRAND.bgWhite;
        } else if (variant === 'ghost') {
          e.currentTarget.style.background = 'transparent';
        } else {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 6px 20px rgba(249,115,22,0.35)';
        }
      }}
    >
      <Play size={sizing.icon} fill="currentColor" />
      {label}
    </button>
  );
}
